import type {
  NoteAttachment,
  NoteBody,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import type {
  ChipAttachment,
  RegistryEntry,
} from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';

import { extractPlaintextFromBody } from './coachNotes.utils';
import { transformBodyForSave, walkLexical } from './noteBody.transform';

export type NoteSavePayload = Readonly<{
  body: NoteBody;
  body_plaintext: string;
  attachment_ids: number[];
}>;

// Build the API payload for a draft, or null when it can't be saved yet
// (no body, or an inline image whose upload hasn't finished).
export const buildNoteSavePayload = (
  body: NoteBody | null,
  chips: readonly ChipAttachment[],
  registry: readonly RegistryEntry[],
): NoteSavePayload | null => {
  if (!body) return null;

  const transformed = transformBodyForSave(body, new Map(registry));

  if (!transformed.ok) return null;

  const attachment_ids = chips.flatMap((c) => (c.status === 'done' ? [c.mediaId] : []));

  return {
    body: transformed.body,
    body_plaintext: extractPlaintextFromBody(body),
    attachment_ids,
  };
};

export const attachmentsToChips = (
  attachments: readonly NoteAttachment[] | undefined,
): ChipAttachment[] =>
  (attachments ?? [])
    .filter((a) => !a.is_inline)
    .map((a) => ({
      localId: `existing-${a.id}`,
      mediaId: a.id,
      filename: a.name,
      size: a.size,
      status: 'done' as const,
    }));

export const registryEntriesFromBody = (body: NoteBody | null): RegistryEntry[] => {
  const entries: RegistryEntry[] = [];

  walkLexical(body, (node) => {
    if (
      node.type === 'image' &&
      typeof node.src === 'string' &&
      typeof node.media_id === 'number'
    ) {
      entries.push([node.src, node.media_id]);
    }
  });

  return entries;
};
